import { zodResolver } from '@hookform/resolvers/zod';
import {
  type CountryCode,
  isValidPhoneNumber,
  parsePhoneNumberFromString,
} from 'libphonenumber-js';
import { Duration } from 'luxon';
import type { FieldValues, Resolver } from 'react-hook-form';
import slugify from 'slugify';
import { z } from 'zod';
import { errorMap } from 'zod-validation-error';

import { parseISO8601DateTime, parseISO8601Duration } from '@/utils/dates';
import { AnchorDuration, isAnchorDuration } from '@/utils/pricing';

export const zodEnum = <T>(arr: T[] | readonly T[]): [T, ...T[]] =>
  arr as [T, ...T[]];

export type APIDurationType = z.infer<typeof zodDuration>;

const baseDurationSchema = z.coerce
  .string()
  .transform((val) => {
    if (isAnchorDuration(val)) {
      return val;
    }
    return parseISO8601Duration(val);
  })
  .refine<Duration<true> | AnchorDuration>(
    (duration): duration is Duration<true> | AnchorDuration => {
      if (isAnchorDuration(duration)) {
        return true;
      }
      if (duration instanceof Duration) {
        return duration.isValid;
      }
      return false;
    },
    { message: 'Duration is invalid' }
  );

export const zodDuration = baseDurationSchema
  .refine(
    (duration) =>
      isAnchorDuration(duration) ||
      (duration instanceof Duration && duration.as('milliseconds') !== 0),
    { message: 'Duration cannot be zero' }
  )
  .or(
    z
      .literal('all_day')
      .or(z.literal('until_5am_next_day'))
      .or(z.literal('usage'))
  );

// Narrower variant for pricing-item schemas: rejects 'usage' so the leaked
// type and the form-runtime stay aligned. 'usage' is a sublocation-level
// billing methodology, not a fixed-tariff duration, and should never appear
// on a list/tiered pricing item.
export const zodPricingItemDuration = baseDurationSchema
  .refine(
    (duration) =>
      isAnchorDuration(duration) ||
      (duration instanceof Duration && duration.as('milliseconds') !== 0),
    { message: 'Duration cannot be zero' }
  )
  .or(z.literal('all_day').or(z.literal('until_5am_next_day')));

// Used by tiered pricing `from_duration` / `to_duration` (whole-hour Luxon
// durations, including PT0S). Do not add preset literals here: that widens
// Pricing types and breaks ParkingTariffDuration consumers.
export const zodDurationAllowZero = baseDurationSchema;

// Same as zodDuration (including all_day / until_5am_next_day / usage) but
// allows PT0S. Validations can persist zero stay for usage-based immediate exit.
export const zodValidationDuration = baseDurationSchema.or(
  z
    .literal('all_day')
    .or(z.literal('until_5am_next_day'))
    .or(z.literal('usage'))
);

export const zodTimeStamp = z.coerce.string().transform((val, ctx) => {
  const dateTime = parseISO8601DateTime(val);
  if (!dateTime.isValid) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Not a valid timestamp',
    });

    return z.NEVER;
  }
  return dateTime;
});

export const zodStringRequired = (msg?: string) =>
  z
    .string()
    .trim()
    .min(1, { message: msg ?? 'Required!' });

/**
 * Optional integer for number inputs, which report a cleared field as ''.
 * A blank input parses to null instead of coercing to 0.
 */
export const zodBlankableInt = ({
  min,
  max,
}: { min?: number; max?: number } = {}) => {
  let schema = z.coerce.number().int();
  if (min !== undefined) schema = schema.min(min);
  if (max !== undefined) schema = schema.max(max);
  return z.preprocess(
    (val) => (val === '' ? null : val),
    schema.nullable().optional()
  );
};

/**
 * Optional plan eligibility label (free text shown to members when a plan
 * requires eligibility verification). Shared by the plan input and response
 * contracts so the length cap stays in lockstep and an accepted submission can
 * never parse as an invalid response.
 */
export const ELIGIBILITY_LABEL_MAX_LENGTH = 100;

export const zodEligibilityLabel = z
  .string()
  .max(ELIGIBILITY_LABEL_MAX_LENGTH)
  .nullable()
  .optional();

/**
 * 7-character CSS hex color (`#RRGGBB`), matching the validation rule of the
 * native `<input type="color">` element (see MDN). Use `.optional()` /
 * `.nullish()` at the call site for optional fields.
 */
export const zodHexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, {
  message:
    'Invalid color format. Must be a 7-character hex code (e.g., #RRGGBB).',
});

const PHONE_INVALID_MESSAGE =
  'Please enter a valid phone number in international format (e.g., +1234567890)';

const phoneNumberValidator =
  (defaultCountry?: CountryCode) => (value: string) =>
    !value
      ? false
      : defaultCountry
        ? isValidPhoneNumber(value, defaultCountry)
        : isValidPhoneNumber(value);

/**
 * Factory for an E164 phone schema. When `defaultCountry` is provided the
 * leading `+countryCode` becomes optional and the value is validated against
 * the supplied country. Without a default the value must already be in
 * full E.164 form (e.g. `+12345678901`).
 */
export const e164PhoneSchemaFor = (defaultCountry?: CountryCode) =>
  z
    .string()
    .min(1, 'Phone number is required')
    .refine(phoneNumberValidator(defaultCountry), {
      message: PHONE_INVALID_MESSAGE,
    });

/**
 * Custom Zod schema for E164 phone number validation using libphonenumber-js
 * E164 format: +[country code][phone number]
 * Example: +12345678901
 */
export const e164PhoneSchema = e164PhoneSchemaFor();

/**
 * Factory for an optional phone schema. Empty string and undefined are
 * accepted; any non-empty value must be a valid phone number. When a
 * `defaultCountry` is supplied callers may type the local form (e.g.
 * `(555) 123-4567`) and the schema parses against that country.
 */
export const optionalE164PhoneSchemaFor = (defaultCountry?: CountryCode) =>
  z
    .string()
    .refine(
      (value) =>
        !value ||
        (defaultCountry
          ? isValidPhoneNumber(value, defaultCountry)
          : isValidPhoneNumber(value)),
      { message: PHONE_INVALID_MESSAGE }
    )
    .optional()
    .or(z.literal(''));

/**
 * Optional E164 phone schema. Empty string and undefined are accepted; any
 * non-empty value must be a valid international phone number.
 */
export const optionalE164PhoneSchema = optionalE164PhoneSchemaFor();

/**
 * Transform a phone number string to E164 format
 * @param phoneNumber - The phone number string to transform
 * @param defaultCountry - Default country code (default: 'US')
 * @returns E164 formatted phone number or null if invalid
 */
export const formatToE164 = (
  phoneNumber: string,
  defaultCountry: CountryCode = 'US'
): string | null => {
  if (!phoneNumber?.trim()) return null;

  const parsed = parsePhoneNumberFromString(phoneNumber, defaultCountry);
  return parsed?.isValid() === true ? parsed.format('E.164') : null;
};

/**
 * Check if a phone number is valid E164 format
 * @param phoneNumber - The phone number to validate
 * @returns boolean indicating if the phone number is valid E164
 */
export const isValidE164 = (phoneNumber: string): boolean => {
  if (!phoneNumber) return false;

  return isValidPhoneNumber(phoneNumber);
};

/**
 * Required email address. Surrounding whitespace is the caller's to trim
 * before parsing, so the stored value is exactly what was validated.
 */
export const requiredEmailSchema = z
  .string()
  .min(1, 'Email address is required')
  .email('Enter a valid email address');

// `String.prototype.trim` keeps zero-width characters, which come along when an
// address is copied out of an email client or a chat message.
const SURROUNDING_EMAIL_WHITESPACE =
  /^[\s\u200B-\u200D\u2060]+|[\s\u200B-\u200D\u2060]+$/g;

/** Strips surrounding whitespace, including non-breaking and zero-width characters. */
export const trimEmail = (value: string) =>
  value.replace(SURROUNDING_EMAIL_WHITESPACE, '');

/** Email address, trimmed with `trimEmail` before it is checked. */
export const zodTrimmedEmail = z
  .string()
  .transform(trimEmail)
  .pipe(z.string().email());

/** US license-plate validation. Trims, enforces alphanumeric, and caps length. */
export const PLATE_MAX_LENGTH = 10;

export const PLATE_CHARS_MESSAGE =
  'Plate must contain only letters and numbers. Leave out any special characters.';

export const zodPlate = z
  .string()
  .trim()
  .min(1, 'Plate is required')
  .max(PLATE_MAX_LENGTH, `Plate cannot exceed ${PLATE_MAX_LENGTH} characters`)
  .regex(/^[A-Za-z0-9]+$/, PLATE_CHARS_MESSAGE)
  .transform((v) => v.toUpperCase());

// Lazy-load the state list so the table (and the `@/constants` module it
// lives in) is only pulled into bundles that actually validate plate states.
// On rejection (e.g. a failed chunk fetch) drop the cached promise so the
// next call retries instead of permanently breaking validation.
let plateStateAbbrsPromise: Promise<Set<string>> | null = null;
const getPlateStateAbbrs = (): Promise<Set<string>> => {
  if (!plateStateAbbrsPromise) {
    const pending = import('@/constants').then(
      ({ PLATE_STATE_OPTIONS }) =>
        new Set(PLATE_STATE_OPTIONS.map((s) => s.abbreviation))
    );
    pending.catch(() => {
      if (plateStateAbbrsPromise === pending) {
        plateStateAbbrsPromise = null;
      }
    });
    plateStateAbbrsPromise = pending;
  }
  return plateStateAbbrsPromise;
};

/**
 * Plate-state/province abbreviation validation (US + Canadian jurisdictions).
 * Uses an async refine + a lazy dynamic import so the PLATE_STATE_OPTIONS table
 * is not bundled into callers that don't validate plate states. Pair with
 * `zodResolver` or `parseAsync`, since sync `.parse()` will throw on async schemas.
 */
export const zodPlateState = z
  .string()
  .min(2, 'Select a plate state')
  .refine(async (v) => (await getPlateStateAbbrs()).has(v), {
    message: 'Select a valid plate state or province',
  });

interface SlugSchemaInputs {
  min?: number;
  max?: number;
  field?: string;
}

export const slugSchema = ({
  min = 1,
  max = 64,
  field = 'Slug',
}: SlugSchemaInputs = {}) => {
  return z
    .string()
    .min(min, {
      message: `${field} field must be at least ${min} character${min === 1 ? '' : 's'}`,
    })
    .max(max, {
      message: `${field} field must be at most ${max} character${max === 1 ? '' : 's'}`,
    })
    .refine(
      (v) => {
        return (
          slugify(v, { lower: false, strict: true, replacement: '' }) === v
        );
      },
      {
        message: `${field} field can only contain letters and numbers`,
      }
    );
};

/**
 * A stable resolver for a schema that depends on runtime data. Pass that data
 * as `useForm({ context })`: React Hook Form hands the latest context to the
 * resolver on every validation, so the schema never goes stale.
 */
export const zodContextResolver =
  <TFieldValues extends FieldValues, TContext>(
    buildSchema: (context: TContext) => z.ZodTypeAny
  ): Resolver<TFieldValues, TContext> =>
  (values, context, options) => {
    if (context === undefined) {
      throw new Error(
        'zodContextResolver requires the useForm `context` option'
      );
    }
    return zodResolver(buildSchema(context), { errorMap })(
      values,
      context,
      options
    );
  };
